import {Button, Col, Drawer, Form, Input, Modal, notification, Row, Upload, UploadFile} from 'antd';
import React, { useEffect, useState } from 'react';
import { ActionType } from '../../constants/actionType';
import './CharacterDrawer.scss';
import {RcFile, UploadProps} from "antd/es/upload";
import {Icon} from "ts-react-feather-icons";
import {createCharacterOutfit, updateCharacterOutfit} from '../../helpers/api/character';
import {convertToWebP} from "../../utils/image";

const outfitDrawerValidation = {
    name: [
        { required: true, message: 'Name is required' },
        { max: 100, message: 'Name must be less than 100 characters' },
    ],
}

const OutfitDrawer = ({
    character,
    drawerVisibility,
    setdDrawerVisibility,
    outfitDetail,
    setOutfitDetail,
    actionType,
    setActionType,
    onSuccess,
    onFailure
}: any) => {

    const [form] = Form.useForm();
    const formValueObj = Form.useWatch([], form);

    const [submittables, setSubmittable] = useState<boolean>(false);
    const [loadingBtn, setLoadingBtn] = useState<boolean>(false);

    // for image
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [images, setImages] = useState<UploadFile[]>([]);

    // images
    const getBase64 = (file: RcFile): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });

    const handleCancel = () => setPreviewOpen(false);

    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as RcFile);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    };

    const handleChange: UploadProps['onChange'] = async ({ fileList: newFileList }) => {
        try {
            // Tạo một danh sách fileList tạm thời để hiển thị ngay lập tức
            const tempFileList = newFileList.map(file => ({
                ...file,
                status: 'uploading' as UploadFile<any>['status'],
            }));
            setImages(tempFileList as UploadFile<any>[]);

            // Convert files sang WebP
            const webpFiles = await Promise.all(
                newFileList.map(async (file) => {
                    // Chỉ convert nếu là file mới upload
                    if (!file.originFileObj) {
                        return file;
                    }

                    try {
                        const webpFile = await convertToWebP(file.originFileObj as RcFile);
                        // Tạo preview URL cho file đã convert
                        const previewUrl = await getBase64(webpFile);

                        return {
                            ...file,
                            originFileObj: webpFile,
                            status: 'done',
                            preview: previewUrl,
                            url: previewUrl,
                            fileName: webpFile.name
                        };
                    } catch (error) {
                        return {
                            ...file,
                            status: 'error',
                            error: new Error('Failed to convert image')
                        };
                    }
                })
            );

            setImages(webpFiles as UploadFile<any>[]);
        } catch (error) {
            console.error('Error processing files:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to process image'
            });
        }
    };

    const uploadButton = (
        <div>
            {/* <PlusOutlined rev={1} /> */}
            <Icon name="plus" size={16}/>
            <div style={{marginTop: 8}}>Upload</div>
        </div>
    );

    useEffect(() => {
        form.validateFields({ validateOnly: true })
            .then(
                () => { setSubmittable(true) },
                () => { setSubmittable(false) },
            );
    }, [formValueObj]);

    useEffect(() => {
        if (actionType === ActionType.CREATE) {
            form.resetFields();
            setImages([]);
        }
    }, [actionType]);

    useEffect(() => {
        if (!outfitDetail) {
            return;
        }
        if (outfitDetail?.imageUrl && outfitDetail?.imageUrl?.trim().length > 0) {
            const urlTemp = outfitDetail?.imageUrl.split("/");
            setImages([{
                uid: "1",
                name: urlTemp[urlTemp.length - 1],
                status: 'done',
                url: outfitDetail?.imageUrl
            }]);
        } else {
            setImages([]);
        }
        form.setFieldsValue({ ...outfitDetail });
    }, [outfitDetail]);
    
    const onClose = () => {
        form.resetFields();
        setOutfitDetail(null);
        setSubmittable(false);
        setImages([]);
        setdDrawerVisibility(false);
        setActionType("");
    }

    const saveOutfit = async () => {
        try {
            setLoadingBtn(true);
            let response;
            const requestModel: any = {
                name: formValueObj.name?.trim()
            }
            if (actionType === ActionType.CREATE) {
                response = await createCharacterOutfit(character.id, {
                    data: JSON.stringify(requestModel),
                    image: images.length > 0 ? images?.[0]?.originFileObj : null
                });
            } else {
                const requestBody: any = {};
                if (images?.[0] && images?.[0].url === outfitDetail.imageUrl) {
                    requestModel["imageUrl"] = outfitDetail.imageUrl;
                } else {
                    requestBody["image"] = images?.[0]?.originFileObj;
                }
                requestBody["data"] = JSON.stringify(requestModel);

                response = await updateCharacterOutfit(character.id, outfitDetail.id, requestBody);
            }

            setLoadingBtn(false);
            if (response?.status === 200) {
                onSuccess();
                onClose();
                return;
            }
        } catch (err) {
            console.log(err);
            onFailure(err);
        }
        setLoadingBtn(false);
    }

    return (
        <Drawer
            title={(actionType === ActionType.UPDATE ? "Update" : "Create") + " outfit"}
            className='outfit-drawer'
            width={850}
            onClose={() => { setdDrawerVisibility(false) }}
            open={drawerVisibility}
            closeIcon={false}
            footer={
                <div className='d-flex justify-content-center'>
                    <Button onClick={saveOutfit} type="primary" className='me-3' disabled={!submittables} loading={loadingBtn}>
                        Save
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </div>
            }
        >
            <Form layout="vertical" autoComplete='off' form={form}>
                <Row gutter={16} className='d-flex justify-content-center'>
                    <Form.Item
                        name="image"
                        label="Image"
                    >
                        <Upload
                            listType="picture-card"
                            fileList={images}
                            onPreview={handlePreview}
                            onChange={handleChange}
                            multiple={false}
                            accept="image/png, image/jpeg, image/jpg"
                            disabled={ActionType.UPDATE === actionType}
                        >
                            {images.length >= 1 ? null : uploadButton}
                        </Upload>
                        <Modal open={previewOpen} footer={null} onCancel={handleCancel}>
                            <img alt="example" style={{width: '100%'}} src={previewImage}/>
                        </Modal>
                    </Form.Item>
                </Row>
                <Row gutter={16} className='mb-3'>
                    <Col span={24}>
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={outfitDrawerValidation.name}
                        >
                            <Input placeholder="Please enter name" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    )

}

export default OutfitDrawer;
